import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAuthOtpStore = defineStore('authOtp', () => {
  // State
  const strategy = ref(null) // 'phone' or 'email'
  const error = ref(null)
  const countryCode = ref(null)
  const phone = ref(null)
  const email = ref(null)
  const target = ref(null)
  const requestId = ref(null)
  const ttl = ref(0)
  const needPhone = ref(false)
  const needEmail = ref(false)
  const loading = ref(false)
  const needAcknowledge = ref(true)
  const verifyStrategy = ref(null)
  const verifyCheckpoint = ref(null)

  // interval ID（不需要是 ref，因為不需要響應式）
  let countdownTimerId = null

  // Promise resolver（用於等待 OTP 驗證完成）
  let pendingResolver = null

  // Getters
  const getStrategy = computed(() => strategy.value)
  const getError = computed(() => error.value)
  const getCountryCode = computed(() => countryCode.value)
  const getPhone = computed(() => phone.value)
  const getEmail = computed(() => email.value)
  const getTarget = computed(() => target.value)
  const getRequestId = computed(() => requestId.value)
  const getTtl = computed(() => ttl.value)
  const getNeedPhone = computed(() => needPhone.value)
  const getNeedEmail = computed(() => needEmail.value)
  const getLoading = computed(() => loading.value)
  const getNeedAcknowledge = computed(() => needAcknowledge.value)
  const getVerifyStrategy = computed(() => verifyStrategy.value)
  const getVerifyCheckpoint = computed(() => verifyCheckpoint.value)

  // Actions
  function setStrategy(value) {
    strategy.value = value
  }

  function setError(value) {
    error.value = value
  }

  function setCountryCode(value) {
    countryCode.value = value
  }

  function setPhone(value) {
    phone.value = value
  }

  function setEmail(value) {
    email.value = value
  }

  function setTarget(value) {
    target.value = value
  }

  function setRequestId(value) {
    requestId.value = value
  }

  function setTtl(value) {
    ttl.value = value
  }

  function setNeedPhone(value) {
    needPhone.value = value
  }

  function setNeedEmail(value) {
    needEmail.value = value
  }

  function setLoading(value) {
    loading.value = value
  }

  function setNeedAcknowledge(value) {
    needAcknowledge.value = value
  }

  function setVerifyStrategy(value) {
    verifyStrategy.value = value
  }

  function setVerifyCheckpoint(value) {
    verifyCheckpoint.value = value
  }

  /**
   * 開始倒數計時（在 store 中管理，跨元件同步）
   */
  function startCountdown() {
    if (typeof window === 'undefined') {
      return
    }

    // 先停止現有的計時器
    stopCountdown()

    countdownTimerId = setInterval(() => {
      if (ttl.value > 0) {
        ttl.value = Math.max(ttl.value - 1000, 0)
      } else {
        stopCountdown()
      }
    }, 1000)
  }

  /**
   * 停止倒數計時
   */
  function stopCountdown() {
    if (typeof window === 'undefined') {
      return
    }

    if (countdownTimerId) {
      clearInterval(countdownTimerId)
      countdownTimerId = null
    }
  }

  /**
   * 檢查倒數計時是否正在運行
   */
  function isCountdownRunning() {
    return countdownTimerId !== null
  }

  /**
   * 設定 Promise resolver（由 triggerOtpVerification 調用）
   */
  function setPendingResolver(resolver) {
    pendingResolver = resolver
  }

  /**
   * 解決 Promise（由彈窗完成時調用）
   * @param {boolean} success - 驗證是否成功
   */
  function resolvePending(success) {
    if (pendingResolver) {
      pendingResolver(success)
      pendingResolver = null
    }
  }

  /**
   * 檢查是否有待處理的 Promise
   */
  function hasPendingResolver() {
    return pendingResolver !== null
  }

  function resetState() {
    stopCountdown()
    // 如果有待處理的 Promise，以 false 解決
    resolvePending(false)
    strategy.value = null
    error.value = null
    countryCode.value = null
    phone.value = null
    email.value = null
    target.value = null
    requestId.value = null
    ttl.value = 0
    needPhone.value = false
    needEmail.value = false
    loading.value = false
    needAcknowledge.value = true
    verifyStrategy.value = null
    verifyCheckpoint.value = null
  }

  return {
    // State
    strategy,
    error,
    countryCode,
    phone,
    email,
    target,
    requestId,
    ttl,
    needPhone,
    needEmail,
    loading,
    needAcknowledge,
    verifyStrategy,
    verifyCheckpoint,
    // Getters
    getStrategy,
    getError,
    getCountryCode,
    getPhone,
    getEmail,
    getTarget,
    getRequestId,
    getTtl,
    getNeedPhone,
    getNeedEmail,
    getLoading,
    getNeedAcknowledge,
    getVerifyStrategy,
    getVerifyCheckpoint,
    // Actions
    setStrategy,
    setError,
    setCountryCode,
    setPhone,
    setEmail,
    setTarget,
    setRequestId,
    setTtl,
    setNeedPhone,
    setNeedEmail,
    setLoading,
    setNeedAcknowledge,
    setVerifyStrategy,
    setVerifyCheckpoint,
    startCountdown,
    stopCountdown,
    isCountdownRunning,
    setPendingResolver,
    resolvePending,
    hasPendingResolver,
    resetState,
  }
})
